import React from 'react';
import { Typography, Space, Tag, Divider } from 'antd';

const { Text, Paragraph } = Typography;

/**
 * ヘルプコンテンツ定義
 * 各ページ・セクション用のヘルプテキストを集約管理
 */

// ダッシュボード関連
export const dashboardHelp = {
  rankProgress: {
    tooltip: 'ランクとEXPについて',
    title: 'ランクシステムについて',
    content: (
      <div>
        <Paragraph>
          magicssでは、利用状況に応じてEXP（経験値）が貯まり、ランクが上がっていきます。
        </Paragraph>
        <Paragraph strong>EXPの獲得方法：</Paragraph>
        <ul>
          <li>ログイン（1日1回）</li>
          <li>リサーチの完了</li>
          <li>記事生成の完了</li>
          <li>初回チュートリアルの完了</li>
        </ul>
        <Paragraph strong>ランクアップ特典：</Paragraph>
        <ul>
          <li>ブラッシュアップ回数の増加</li>
          <li>スタイル微調整機能の解放</li>
          <li>独自情報（トッピング）機能の解放</li>
          <li>ボーナスクレジットの付与</li>
        </ul>
      </div>
    ),
  },
  statistics: {
    tooltip: '統計情報の見方',
    title: '統計情報について',
    content: (
      <div>
        <Paragraph>
          あなたのmagicss利用状況をまとめた統計です。
        </Paragraph>
        <ul>
          <li><strong>ログイン日数</strong>：累計のログイン日数</li>
          <li><strong>リサーチ数</strong>：完了したリサーチの総数</li>
          <li><strong>生成記事数</strong>：生成完了した記事の総数</li>
          <li><strong>総文字数</strong>：生成した記事の合計文字数</li>
          <li><strong>節約時間</strong>：リサーチ・生成で節約できた推定時間</li>
        </ul>
      </div>
    ),
  },
};

// リサーチ関連
export const researchHelp = {
  keyword: {
    tooltip: '検索キーワードを入力',
    title: 'キーワードについて',
    content: (
      <div>
        <Paragraph>
          リサーチしたい検索キーワードを入力します。
          ユーザーがGoogleで実際に検索するであろうキーワードを入力してください。
        </Paragraph>
        <Paragraph strong>キーワードの例：</Paragraph>
        <ul>
          <li>「東京 カフェ おすすめ」</li>
          <li>「プログラミング 初心者 学習方法」</li>
          <li>「SEO対策 2024」</li>
        </ul>
        <Paragraph type="secondary">
          複合キーワード（2〜3語）の方が、より具体的な検索意図を分析できます。
        </Paragraph>
      </div>
    ),
  },
  serpAnalysis: {
    tooltip: '検索結果の傾向分析',
    title: 'SERP分析について',
    content: (
      <div>
        <Paragraph>
          SERP（Search Engine Results Page）分析では、検索結果ページの特徴を分析します。
        </Paragraph>
        <ul>
          <li><strong>サイトタイプ</strong>：企業サイト、メディア、個人ブログなどの分布</li>
          <li><strong>平均文字数</strong>：上位サイトの平均的なコンテンツ量</li>
          <li><strong>SEO難易度</strong>：このキーワードで上位表示する難しさ</li>
        </ul>
      </div>
    ),
  },
  searchIntent: {
    tooltip: '検索意図とは',
    title: '検索意図の分析について',
    content: (
      <div>
        <Paragraph>
          検索意図とは、ユーザーがそのキーワードで「何を求めているか」です。
          現代のSEOでは最も重要な要素の一つです。
        </Paragraph>
        <Paragraph strong>検索意図の種類：</Paragraph>
        <ul>
          <li><strong>情報収集型</strong>：知識や情報を得たい</li>
          <li><strong>購買検討型</strong>：商品やサービスを比較・検討したい</li>
          <li><strong>行動型</strong>：具体的なアクションを起こしたい</li>
        </ul>
        <Paragraph type="secondary">
          検索意図に合致したコンテンツを作成することで、上位表示の可能性が高まります。
        </Paragraph>
      </div>
    ),
  },
  topicMap: {
    tooltip: 'トピック構造の見方',
    title: 'トピック構造マップについて',
    content: (
      <div>
        <Paragraph>
          上位サイトで扱われているトピック（話題・情報）を可視化した図です。
        </Paragraph>
        <ul>
          <li>円の大きさ = そのトピックの重要度（多くのサイトで扱われている）</li>
          <li>中心に近い = より基本的・必須のトピック</li>
          <li>外側 = より詳細・発展的なトピック</li>
        </ul>
        <Paragraph type="secondary">
          上部のスライダーで、表示するトピックの数を調整できます。
        </Paragraph>
      </div>
    ),
  },
};

// 生成STEP1関連
export const generationStep1Help = {
  researchSelect: {
    tooltip: 'リサーチ結果を選択',
    title: 'リサーチ結果の選択について',
    content: (
      <div>
        <Paragraph>
          事前に実行したリサーチ結果を選択すると、競合分析データを活用した高品質な記事を生成できます。
        </Paragraph>
        <Paragraph strong>リサーチ結果を使うメリット：</Paragraph>
        <ul>
          <li>検索意図に沿った構成案の生成</li>
          <li>競合サイトのトピックカバレッジを考慮</li>
          <li>構成案のスコアリング機能が利用可能</li>
          <li>トピック・キーワードの参照パネルが利用可能</li>
        </ul>
        <Paragraph type="warning">
          リサーチなしでも生成は可能ですが、品質や利用できる機能が制限されます。
        </Paragraph>
      </div>
    ),
  },
  style: {
    tooltip: 'スタイルについて',
    title: 'スタイル設定について',
    content: (
      <div>
        <Paragraph>
          スタイルは記事の文体・口調・構成の傾向を決定する設定です。
        </Paragraph>
        <Paragraph strong>スタイルで設定できる項目：</Paragraph>
        <ul>
          <li><strong>文体トーン</strong>：フォーマル〜カジュアル</li>
          <li><strong>専門性</strong>：初心者向け〜専門家向け</li>
          <li><strong>文末表現</strong>：です・ます調 / だ・である調</li>
          <li><strong>接続詞の使用頻度</strong>：少なめ〜多め</li>
          <li><strong>箇条書き・表の使用頻度</strong></li>
        </ul>
        <Paragraph type="secondary">
          ランクアップすると、独自のスタイルを登録できるようになります。
        </Paragraph>
      </div>
    ),
  },
};

// 生成STEP3関連
export const generationStep3Help = {
  outline: {
    tooltip: '構成案の編集方法',
    title: '構成案の編集について',
    content: (
      <div>
        <Paragraph>
          記事の見出し構成を編集できます。良い構成は良い記事の土台です。
        </Paragraph>
        <Paragraph strong>編集操作：</Paragraph>
        <ul>
          <li><strong>並び替え</strong>：左のアイコンをドラッグ＆ドロップ</li>
          <li><strong>追加・削除</strong>：右の「…」メニューから</li>
          <li><strong>見出しレベル変更</strong>：H2/H3/H4のラベルをクリック</li>
          <li><strong>メモ追加</strong>：各見出しにメモを追加可能</li>
        </ul>
        <Paragraph type="secondary">
          テキストモードに切り替えると、テキスト形式でまとめて編集できます。
        </Paragraph>
      </div>
    ),
  },
  scoring: {
    tooltip: 'スコアリングについて',
    title: '構成案スコアリングについて',
    content: (
      <div>
        <Paragraph>
          AIが構成案を評価し、2つのスコアを算出します。
        </Paragraph>
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Tag color="blue">カバレッジスコア</Tag>
            <Text>
              競合サイトが扱っているトピックをどれだけカバーしているか
            </Text>
          </div>
          <div>
            <Tag color="purple">ユニークネススコア</Tag>
            <Text>
              競合と差別化できる独自のトピックがあるか
            </Text>
          </div>
        </Space>
        <Divider />
        <Paragraph type="secondary">
          「分析画面を開く」で詳細な改善提案を確認できます。
        </Paragraph>
      </div>
    ),
  },
  topicPanel: {
    tooltip: 'トピックパネルの使い方',
    title: 'トピックパネルについて',
    content: (
      <div>
        <Paragraph>
          リサーチで分析されたトピックが表示されます。
          構成案に盛り込むべきトピックの参考にしてください。
        </Paragraph>
        <ul>
          <li>カテゴリータブで分類を切り替え</li>
          <li>スコアスライダーで重要度で絞り込み</li>
          <li>クリックで見出しへの追加候補として参照</li>
        </ul>
      </div>
    ),
  },
};

// 生成STEP4.4関連
export const generationStep4_4Help = {
  editor: {
    tooltip: 'エディタの使い方',
    title: 'リッチテキストエディタについて',
    content: (
      <div>
        <Paragraph>
          生成された記事を自由に編集できます。
        </Paragraph>
        <Paragraph strong>主な機能：</Paragraph>
        <ul>
          <li>見出しの追加・編集（H2〜H4）</li>
          <li>箇条書き・番号付きリスト</li>
          <li>表の作成・編集</li>
          <li>太字・斜体などの装飾</li>
          <li>画像の挿入（右パネルからドラッグ＆ドロップ）</li>
        </ul>
        <Paragraph type="secondary">
          編集内容は自動保存されます。
        </Paragraph>
      </div>
    ),
  },
  imagePanel: {
    tooltip: '画像挿入について',
    title: '画像挿入機能について',
    content: (
      <div>
        <Paragraph>
          Pexelsの無料高品質画像を検索して挿入できます。
        </Paragraph>
        <Paragraph strong>使い方：</Paragraph>
        <ol>
          <li>検索ボックスにキーワードを入力</li>
          <li>表示された画像をドラッグ</li>
          <li>エディタ内の挿入したい位置にドロップ</li>
        </ol>
        <Paragraph type="secondary">
          画像には自動的にクレジット表記が追加されます。
        </Paragraph>
      </div>
    ),
  },
  citations: {
    tooltip: '引用番号について',
    title: '引用番号と参考データについて',
    content: (
      <div>
        <Paragraph>
          本文中の[1][2]などの番号は、AIが参照した情報源を示します。
        </Paragraph>
        <ul>
          <li>引用番号をクリック → 右パネルで出典を確認</li>
          <li>「引用番号を表示」チェックボックスで表示/非表示を切り替え</li>
          <li>エクスポート時にも引用番号の設定が反映されます</li>
        </ul>
        <Paragraph type="secondary">
          参考データパネルからエディタにドラッグ＆ドロップで、引用を追加することもできます。
        </Paragraph>
      </div>
    ),
  },
  export: {
    tooltip: 'エクスポートについて',
    title: 'エクスポート機能について',
    content: (
      <div>
        <Paragraph>
          完成した記事を外部に出力できます。
        </Paragraph>
        <Paragraph strong>エクスポート形式：</Paragraph>
        <ul>
          <li>
            <strong>HTMLコピー</strong>：HTMLタグ付きでクリップボードにコピー。
            WordPressなどのCMSに直接ペーストできます。
          </li>
          <li>
            <strong>Google Docs</strong>：リッチテキスト形式でコピー。
            Googleドキュメントにペーストすると書式が維持されます。
          </li>
        </ul>
        <Paragraph type="warning">
          エクスポート後は「生成を完了する」ボタンで完了マークを付けると、
          EXPを獲得できます。
        </Paragraph>
      </div>
    ),
  },
  complete: {
    tooltip: '生成完了について',
    title: '生成完了について',
    content: (
      <div>
        <Paragraph>
          「生成を完了する」ボタンを押すと、記事が完了状態になります。
        </Paragraph>
        <Paragraph strong>完了すると：</Paragraph>
        <ul>
          <li>EXPを獲得（ランクアップに貢献）</li>
          <li>生成実績としてカウント</li>
          <li>記事は閲覧モードになり編集不可に</li>
          <li>エクスポートは引き続き可能</li>
        </ul>
        <Paragraph type="secondary">
          完了前に十分な確認・編集を行ってください。
        </Paragraph>
      </div>
    ),
  },
};

// スタイル管理関連
export const styleHelp = {
  overview: {
    tooltip: 'スタイル機能について',
    title: 'スタイル機能について',
    content: (
      <div>
        <Paragraph>
          スタイルは記事の文体・口調・構成の傾向を定義する設定です。
          一度作成したスタイルは、すべての記事生成で再利用できます。
        </Paragraph>
        <Paragraph strong>スタイルで設定できる項目：</Paragraph>
        <ul>
          <li>文体トーン（フォーマル〜カジュアル）</li>
          <li>専門性レベル（初心者向け〜専門家向け）</li>
          <li>文末表現の傾向（です・ます / だ・である）</li>
          <li>接続詞の使用頻度</li>
          <li>箇条書き・表・装飾の使用頻度</li>
        </ul>
      </div>
    ),
  },
};

export default {
  dashboard: dashboardHelp,
  research: researchHelp,
  generationStep1: generationStep1Help,
  generationStep3: generationStep3Help,
  generationStep4_4: generationStep4_4Help,
  style: styleHelp,
};
